import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { socioService, Socio } from '../services/socioService';
import { useAuth } from '../contexts/AuthContext';

const Socios: React.FC = () => {
  const [socios, setSocios] = useState<Socio[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSocio, setSelectedSocio] = useState<Socio | null>(null);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<Socio>>({
    nome_completo: '',
    cpf: '',
    telefone: '',
    email: '',
    cidade: '',
    estado: '',
    cr: '',
    data_valido_ate: '',
    observacoes: '',
  });

  useEffect(() => {
    loadSocios();
  }, []);

  // Calcular data válido até para novo sócio (1 ano a partir de hoje)
  const dataValidoAteDefault = useMemo(() => {
    const hoje = new Date();
    const umAnoDepois = new Date(hoje);
    umAnoDepois.setFullYear(hoje.getFullYear() + 1);
    return umAnoDepois.toISOString().split('T')[0];
  }, []);

  // Preencher data_valido_ate automaticamente quando abrir diálogo para novo sócio
  useEffect(() => {
    if (openDialog && !selectedSocio && !formData.data_valido_ate) {
      setFormData(prev => ({ ...prev, data_valido_ate: dataValidoAteDefault }));
    }
  }, [openDialog, selectedSocio, dataValidoAteDefault, formData.data_valido_ate]);

  const loadSocios = async () => {
    try {
      setLoading(true);
      const data = await socioService.listar();
      setSocios(data);
    } catch (err: any) {
      setError('Erro ao carregar sócios');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (socio?: Socio) => {
    if (socio) {
      setSelectedSocio(socio);
      // Garantir que todos os campos sejam incluídos, incluindo CR e status
      setFormData({
        nome_completo: socio.nome_completo || '',
        cpf: socio.cpf || '',
        rg: socio.rg || '',
        telefone: socio.telefone || '',
        email: socio.email || '',
        endereco: socio.endereco || '',
        cidade: socio.cidade || '',
        estado: socio.estado || '',
        cep: socio.cep || '',
        cr: socio.cr || '',
        data_valido_ate: socio.data_valido_ate || '',
        observacoes: socio.observacoes || '',
        status: socio.status || 'Ativo',
      });
    } else {
      setSelectedSocio(null);
      // Calcular data válido até (1 ano a partir de hoje) para novo sócio
      const hoje = new Date();
      const umAnoDepois = new Date(hoje);
      umAnoDepois.setFullYear(hoje.getFullYear() + 1);
      const dataValidoAte = umAnoDepois.toISOString().split('T')[0];
      
      setFormData({
        nome_completo: '',
        cpf: '',
        telefone: '',
        email: '',
        cidade: '',
        estado: '',
        cr: '',
        data_valido_ate: dataValidoAte, // Preencher automaticamente
        observacoes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSocio(null);
      setFormData({
        nome_completo: '',
        cpf: '',
        telefone: '',
        email: '',
        cidade: '',
        estado: '',
        cr: '',
        data_valido_ate: '',
        observacoes: '',
      });
  };

  const handleSubmit = async () => {
    try {
      setError('');
      // Preparar dados para envio
      const dadosEnvio = { ...formData };
      delete (dadosEnvio as { acesso_todos?: boolean }).acesso_todos;
      
      // Remover campos vazios de data_valido_ate se não foi informado
      if (!dadosEnvio.data_valido_ate || dadosEnvio.data_valido_ate === '') {
        delete dadosEnvio.data_valido_ate;
      }
      
      // Remover campos vazios de CR se não foi informado
      if (!dadosEnvio.cr || dadosEnvio.cr === '') {
        delete dadosEnvio.cr;
      }
      
      if (selectedSocio) {
        await socioService.atualizar(selectedSocio.id!, dadosEnvio);
      } else {
        await socioService.criar(dadosEnvio as Socio);
      }
      handleCloseDialog();
      loadSocios();
    } catch (err: any) {
      // Tratar erros de validação do FastAPI (422)
      let errorMessage = 'Erro ao salvar sócio';
      
      // Função para traduzir mensagens de erro
      const traduzirErro = (msg: string, field: string): string => {
        // Traduzir nomes de campos
        const campos: { [key: string]: string } = {
          'nome_completo': 'Nome Completo',
          'cpf': 'CPF',
          'email': 'Email',
          'telefone': 'Telefone',
          'cidade': 'Cidade',
          'estado': 'Estado',
          'cep': 'CEP',
          'endereco': 'Endereço',
          'data_valido_ate': 'Válido até',
          'cr': 'CR',
          'rg': 'RG',
          'data_nascimento': 'Data de Nascimento',
          'observacoes': 'Observações',
        };
        
        const campoTraduzido = campos[field] || field;
        const msgLower = msg.toLowerCase();
        
        // Traduzir mensagens de erro específicas
        if (msgLower.includes('field required')) {
          return `${campoTraduzido}: campo obrigatório`;
        }
        
        if (msgLower.includes('value is not a valid email address') || 
            msgLower.includes('email address must have an @-sign') ||
            msgLower.includes('not a valid email')) {
          return `${campoTraduzido}: endereço de email inválido (deve conter @)`;
        }
        
        if (msgLower.includes('string does not match expected pattern')) {
          return `${campoTraduzido}: formato inválido`;
        }
        
        if (msgLower.includes('ensure this value has at least')) {
          const match = msg.match(/at least (\d+)/);
          if (match) {
            return `${campoTraduzido}: deve ter pelo menos ${match[1]} caracteres`;
          }
          return `${campoTraduzido}: valor muito curto`;
        }
        
        if (msgLower.includes('ensure this value has at most')) {
          const match = msg.match(/at most (\d+)/);
          if (match) {
            return `${campoTraduzido}: deve ter no máximo ${match[1]} caracteres`;
          }
          return `${campoTraduzido}: valor muito longo`;
        }
        
        if (msgLower.includes('invalid date format') || msgLower.includes('invalid date')) {
          return `${campoTraduzido}: formato de data inválido`;
        }
        
        // Se não encontrou tradução específica, retornar mensagem genérica
        return `${campoTraduzido}: ${msg}`;
      };
      
      if (err.response?.status === 422 && err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          // Se for array de erros, formatar as mensagens
          const errorMessages = detail.map((e: any) => {
            const field = e.loc && e.loc.length > 1 ? e.loc[e.loc.length - 1] : 'campo';
            // Pegar a mensagem completa, incluindo ctx se existir
            let msgCompleta = e.msg;
            if (e.ctx && typeof e.ctx === 'object') {
              // Se houver contexto adicional, adicionar à mensagem
              if (e.ctx.reason) {
                msgCompleta = `${e.msg}: ${e.ctx.reason}`;
              }
            }
            const msgTraduzida = traduzirErro(msgCompleta, field);
            return msgTraduzida;
          }).join('; ');
          errorMessage = `Erros de validação: ${errorMessages}`;
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = 'Erro de validação. Verifique os campos obrigatórios (Nome Completo e CPF são obrigatórios).';
        }
      } else if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else {
          errorMessage = 'Erro ao salvar sócio';
        }
      }
      
      setError(errorMessage);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja deletar este sócio?')) {
      try {
        await socioService.deletar(id);
        loadSocios();
      } catch (err: any) {
        setError('Erro ao deletar sócio');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 1, sm: 0 }}
        mb={3}
      >
        <Typography variant="h4">Sócios</Typography>
        {user?.acesso_todos && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Novo Sócio
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>CPF</TableCell>
              <TableCell>CR</TableCell>
              <TableCell>Associado desde</TableCell>
              <TableCell>Válido até</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {socios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Nenhum sócio cadastrado
                </TableCell>
              </TableRow>
            ) : (
              socios.map((socio) => {
                const formatDate = (dateStr?: string) => {
                  if (!dateStr) return '-';
                  const date = new Date(dateStr);
                  return date.toLocaleDateString('pt-BR');
                };
                return (
                  <TableRow key={socio.id}>
                    <TableCell>{socio.codigo}</TableCell>
                    <TableCell>{socio.nome_completo}</TableCell>
                    <TableCell>{socio.cpf}</TableCell>
                    <TableCell>{socio.cr || '-'}</TableCell>
                    <TableCell>{formatDate(socio.data_cadastro)}</TableCell>
                    <TableCell>{formatDate(socio.data_valido_ate)}</TableCell>
                    <TableCell>{socio.status || 'Ativo'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(socio)}
                    >
                      <Edit />
                    </IconButton>
                    {user?.acesso_todos && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(socio.id!)}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedSocio ? 'Editar Sócio' : 'Novo Sócio'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {String(error)}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Nome Completo"
            margin="normal"
            required
            value={formData.nome_completo}
            onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
          />
          <TextField
            fullWidth
            label="CPF"
            margin="normal"
            required
            value={formData.cpf}
            onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
          />
          <TextField
            fullWidth
            label="CR (10 dígitos)"
            margin="normal"
            value={formData.cr || ''}
            onChange={(e) => {
              // Permite apenas números e limita a 10 dígitos
              const value = e.target.value.replace(/\D/g, '').slice(0, 10);
              setFormData({ ...formData, cr: value });
            }}
            helperText="Apenas números, será preenchido com zeros à esquerda se necessário"
          />
          {!selectedSocio && (
            <>
              <TextField
                fullWidth
                label="Código do Associado"
                margin="normal"
                value="Será gerado automaticamente"
                InputProps={{ readOnly: true }}
                helperText="Ordem de cadastro (gerado automaticamente ao salvar)"
              />
              <TextField
                fullWidth
                label="Associado desde"
                margin="normal"
                value={new Date().toLocaleDateString('pt-BR')}
                InputProps={{ readOnly: true }}
                helperText="Data de inclusão do associado (data atual)"
              />
            </>
          )}
          {selectedSocio && (
            <>
              <TextField
                fullWidth
                label="Código do Associado"
                margin="normal"
                value={selectedSocio.codigo || ''}
                InputProps={{ readOnly: true }}
                helperText="Ordem de cadastro"
              />
              <TextField
                fullWidth
                label="Associado desde"
                margin="normal"
                value={selectedSocio.data_cadastro ? new Date(selectedSocio.data_cadastro).toLocaleDateString('pt-BR') : ''}
                InputProps={{ readOnly: true }}
                helperText="Data de inclusão do associado"
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status || 'Ativo'}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="Ativo">Ativo</MenuItem>
                  <MenuItem value="Inativo">Inativo</MenuItem>
                  <MenuItem value="Suspenso">Suspenso</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
          <TextField
            fullWidth
            label="Válido até"
            margin="normal"
            type="date"
            value={(() => {
              // Se tem data no formData, usar ela
              if (formData.data_valido_ate) {
                const dateStr = formData.data_valido_ate.toString();
                // Se já está no formato YYYY-MM-DD, retorna direto
                if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
                // Se tem T (datetime), pega só a data
                if (dateStr.includes('T')) return dateStr.split('T')[0];
                // Se é uma data no formato brasileiro ou outro, converte
                try {
                  const date = new Date(dateStr);
                  if (!isNaN(date.getTime())) {
                    return date.toISOString().split('T')[0];
                  }
                } catch {}
              }
              // Se é novo sócio e não tem data, usar a data padrão (1 ano a partir de hoje)
              if (!selectedSocio) {
                return dataValidoAteDefault;
              }
              return '';
            })()}
            onChange={(e) => setFormData({ ...formData, data_valido_ate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            helperText={selectedSocio 
              ? "Data de validade do associado (editável)" 
              : "Data de validade (calculada automaticamente como 1 ano após cadastro, mas pode ser editada)"}
          />
          <TextField
            fullWidth
            label="Telefone"
            margin="normal"
            value={formData.telefone}
            onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
          />
          <TextField
            fullWidth
            label="Email"
            margin="normal"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <TextField
            fullWidth
            label="Cidade"
            margin="normal"
            value={formData.cidade}
            onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
          />
          <TextField
            fullWidth
            label="Estado"
            margin="normal"
            value={formData.estado}
            onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
          />
          <TextField
            fullWidth
            label="Observações"
            margin="normal"
            multiline
            rows={4}
            value={formData.observacoes || ''}
            onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
            helperText="Informações adicionais sobre o associado"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Socios;

